/**
 * Check if the code is running in a browser environment
 */
export const isBrowser =
  typeof window !== "undefined" && typeof document !== "undefined";

/**
 * Get a cookie value by name
 * @param name The name of the cookie to retrieve
 * @returns The cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (!isBrowser) return null;

  try {
    const cookies = document.cookie.split(";");
    for (const cookieStr of cookies) {
      const cookie = cookieStr.trim();
      if (cookie.startsWith(name + "=")) {
        return cookie.substring(name.length + 1);
      }
    }
    return null;
  } catch (error) {
    console.error("Error reading cookie:", error);
    return null;
  }
};

/**
 * Get a wrapper for localStorage with error handling
 * @returns An object with methods to interact with localStorage
 */
export const getLocalStorage = () => {
  return {
    /**
     * Get an item from localStorage
     * @param key The key to retrieve
     * @returns The value or null if not found or an error occurred
     */
    get: (key: string): string | null => {
      if (!isBrowser) return null;
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.error("Error accessing localStorage:", error);
        return null;
      }
    },

    /**
     * Set an item in localStorage
     * @param key The key to set
     * @param value The value to store
     */
    set: (key: string, value: string): void => {
      if (!isBrowser) return;
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.error("Error writing to localStorage:", error);
      }
    },

    /**
     * Remove an item from localStorage
     * @param key The key to remove
     */
    remove: (key: string): void => {
      if (!isBrowser) return;
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error("Error removing from localStorage:", error);
      }
    },
  };
};
